// ─────────────────────────────────────────────
//  assignmentConflict — would SAVING this Add-Assignment form put one CRNA in
//  two rooms at once?
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  WHAT WAS WRONG (reproduced live 2026-08-20, sandbox 60-room board).
//  OR 1 held R. Diaz 07:30–11:00. "＋ Add Assignment" → room OR 4, CRNA
//  R. Diaz, blank start/end → SAVED. No warning, no toast, no marker: the Full
//  Board then asserted that two operating rooms were staffed when one person
//  was in one of them. The picker had offered him with no hint he was already
//  holding a room, and the save said nothing.
//
//  Every OTHER surface on this board already answers this question:
//    · the timeline drag  → `timelineCommit.commitDecision` WARNS on release,
//    · the NEEDS YOU NOW row → `controlPanel` raises it from the same detector,
//    · the assignment GRID → hides an already-booked CRNA from other rows'
//      dropdowns (`AssignmentGrid.js:304` → `assignmentRow.cells.js:48`).
//  This modal was the one write path with none of them.
//
//  ⚠️ NO SECOND DETECTOR LIVES HERE. The clash is decided by
//  `doubleBookedProviders` (doubleBooking.js:52) — the same function that raises
//  the panel row — over a HYPOTHETICAL board, which is the `OutstandingFixModal
//  .js:197` / `timelineCommit.hypotheticalBoard` pattern. The seat is keyed by
//  `crnaSeatKey` (timeline.js:247), never re-derived: three files once computed
//  that key independently and drifted, and the warning went silent on the one
//  row that had two people standing in it. The before/after pair diffing comes
//  from `timelineCommit` too, exported rather than copied.
//
//  Pure — no React, no Firestore.
// ─────────────────────────────────────────────
import { doubleBookedProviders } from "./doubleBooking";
import {
  crnaSeatKey,
  crnaSeatName,
  hmToMinutes,
  to12h,
  DEFAULT_CASE_MIN,
} from "./timeline";
import { pairKeys, pairSignature, labelRooms } from "./timelineCommit";
import { roomForOrRoom } from "./gridTemplate";

const arr = (v) => (Array.isArray(v) ? v : []);

// ── WHAT "OVERLAP" MEANS WHEN A ROW HAS NO TIMES ──────────────────────────────
//
// The reproduced save had BLANK start and end and still conflicted, so this is
// the load-bearing decision in the file, not an edge case.
//
// ⚠️ THE DETECTOR IS SILENT ON UNTIMED ROWS BY DESIGN, AND THAT SILENCE IS WHAT
// SHIPPED THE BUG. `doubleBooking.usable()` (doubleBooking.js:37) drops any row
// without two finite minutes — correct for a warning CARD, which must not assert
// a clash from times nobody entered, and exactly wrong for a SAVE, where
// dropping the row means the operator is told nothing at all.
//
// So a missing start is substituted, not dropped — and the substitution is the
// one the board already makes rather than a new one:
//
//   · NO START TIME → the whole day. `buildTimeline` draws a timeless row with a
//     provider as a watch spanning the entire axis (timeline.js:1182-1228), i.e.
//     a bar that visually crosses every case in the day. [00:00, 24:00) is that
//     same reading, widened to the calendar day so this can never be QUIETER
//     than the chart the operator is looking at (the axis is only ever a subset
//     of the day). Unknown overlaps everything: we cannot rule the clash out,
//     and "cannot rule out" must fail toward the warning.
//
//   · START BUT NO USABLE END → start + DEFAULT_CASE_MIN, byte-for-byte what
//     `buildTimeline` substitutes (timeline.js:1035-1036). Here we are NOT
//     guessing — the operator stated when the person arrives — so the honest
//     window is the one the chart will draw, and widening it to all-day would
//     warn about two bars the operator can plainly see do not touch.
//
// The window is flagged `untimed` so the sentence can say so out loud; a warning
// that quotes 12:00 AM–11:59 PM as if somebody typed it is a lie about data.
const DAY_START_MIN = 0;
const DAY_END_MIN = 24 * 60;

export function conflictWindow(row) {
  const startMin = hmToMinutes(row?.startTime);
  if (startMin == null) {
    return { startMin: DAY_START_MIN, endMin: DAY_END_MIN, untimed: true };
  }
  const rawEnd = hmToMinutes(row?.endTime);
  const endMin = rawEnd == null || rawEnd <= startMin ? startMin + DEFAULT_CASE_MIN : rawEnd;
  return { startMin, endMin, untimed: false };
}

/**
 * The saved day, in the shape `doubleBookedProviders` reads.
 *
 * @param assignments the day's assignment rows
 * @param rooms       the facility's room docs, for resolving orRoom → room id
 * @returns { placed, roomLabels }  `roomLabels` is a Map(roomId → display name)
 *          so the warning can name "OR 1" rather than the room's Firestore id.
 */
export function buildConflictBoard(assignments = [], rooms = []) {
  const roomLabels = new Map();
  const placed = [];
  for (const a of arr(assignments)) {
    if (!a) continue;
    // ⚠️ THE SHIPPED RESOLVER, not a name comparison written here.
    // `roomForOrRoom` ranks (exact → OR-prefix → alias → squashed) and REFUSES a
    // tie, which is how "4" on a row finds the room doc named "OR 4" without a
    // legacy seed room stealing it (roomResolver.js:9-26).
    const room = roomForOrRoom(a.orRoom, rooms);
    // ⚠️ AN UNRESOLVED ROOM IS STILL A ROOM. Falling through to the raw orRoom
    // label keeps the row on the board for comparison purposes; dropping it
    // would make a person in an unmatched room look free, which is the same
    // silence this file exists to remove. Prefixed so a label can never collide
    // with a real Firestore room id.
    const roomId = room ? room.id : `orRoom:${String(a.orRoom || "").trim().toLowerCase()}`;
    const label = room ? room.name : String(a.orRoom || "").trim() || "an unnamed room";
    if (!roomLabels.has(roomId)) roomLabels.set(roomId, label);
    const win = conflictWindow(a);
    placed.push({
      id: a.id ?? null,
      roomId,
      startMin: win.startMin,
      endMin: win.endMin,
      untimed: win.untimed,
      // ⚠️ `crnaSeatKey` / `crnaSeatName`, NEVER `a.ahpId || a.ahpName`. On an
      // orienting row `ahpName` is the whole mashed cell ("Josh Larson 5 Orient
      // Sam Gongora") and naming it as the key makes the preceptor a different
      // person here than on the chart — the precise failure timeline.js:218
      // documents.
      crnaKey: crnaSeatKey(a),
      crnaName: crnaSeatName(a) || null,
    });
  }
  return { placed, roomLabels };
}

/**
 * Would writing this form's seat create a NEW double booking?
 *
 * @param board          `buildConflictBoard(assignments, rooms)` — built once by
 *                       the caller so a 40-provider picker does not rebuild the
 *                       whole day 40 times
 * @param roomId         the room the form is about to write (a room doc id, or
 *                       "__new__" for a room that does not exist yet — which
 *                       matches nothing, correctly)
 * @param roomName       that room's display name, for the sentence
 * @param ahpId/ahpName  the CRNA/AA seat being written
 * @param startTime      "HH:MM" or "" — see `conflictWindow` for what "" means
 * @param endTime        "HH:MM" or ""
 * @param replacesRowId  the assignment row this save OVERWRITES (the upsert
 *                       path's `existing.id`), or null on the create path.
 *                       ⚠️ WITHOUT THIS, EDITING A ROW WARNS ABOUT ITSELF: the
 *                       old row stays on the hypothetical board beside the new
 *                       one, and re-timing OR 1's own case reports OR 1 clashing
 *                       with OR 1 — which `doubleBooking.js:70` happens to skip,
 *                       but a two-room MOVE of the same person would not be.
 *
 * @returns null when the save creates no NEW clash, else
 *          { key, who, roomIds, roomLabels, fromMin, toMin, untimed,
 *            message, note }
 *          `message` is the full sentence for the save-time warning; `note` is
 *          the short "· also in OR 1 · 7:30 AM" suffix for a picker option.
 */
export function seatConflict({
  board,
  roomId,
  roomName = "",
  ahpId = "",
  ahpName = "",
  startTime = "",
  endTime = "",
  replacesRowId = null,
} = {}) {
  const key = crnaSeatKey({ ahpId, ahpName });
  // No seat, no room: an empty CRNA field cannot be in two places, and a form
  // with no room chosen is not about to write anywhere.
  if (!key || !roomId) return null;

  const placed = arr(board && board.placed);
  const roomLabels = (board && board.roomLabels) || new Map();
  const win = conflictWindow({ startTime, endTime });

  // The board as it stands today. A clash that is ALREADY on the board was not
  // created by this save; re-announcing it here would train the operator to
  // click through the warning that means something. Same rule, same reason, as
  // `commitDecision`'s `fresh` filter.
  const before = doubleBookedProviders(placed);
  const wasPaired = pairKeys(before, key);

  const pending = {
    id: replacesRowId ?? "__pending__",
    roomId,
    startMin: win.startMin,
    endMin: win.endMin,
    untimed: win.untimed,
    crnaKey: key,
    crnaName: crnaSeatName({ ahpId, ahpName }) || null,
  };
  // The upsert REPLACES its row; the create path adds beside it.
  const after = doubleBookedProviders([
    ...(replacesRowId ? placed.filter((p) => p.id !== replacesRowId) : placed),
    pending,
  ]);

  const conflict = arr(after).find((c) => c && c.key === key);
  if (!conflict) return null;
  const fresh = arr(conflict.pairs).filter((p) => !wasPaired.has(pairSignature(p)));
  if (!fresh.length) return null;

  // Rooms named in the NEW pairs only, and the window where they actually bite.
  const roomIds = new Set();
  let fromMin = Infinity;
  let toMin = -Infinity;
  for (const p of fresh) {
    roomIds.add(p.a);
    roomIds.add(p.b);
    fromMin = Math.min(fromMin, p.fromMin);
    toMin = Math.max(toMin, p.toMin);
  }
  const labels = new Map(roomLabels);
  if (roomName && !labels.has(roomId)) labels.set(roomId, roomName);
  const otherIds = Array.from(roomIds).filter((id) => id !== roomId);
  const who = crnaSeatName({ ahpId, ahpName }) || conflict.name || "That provider";
  const nameOf = (id) => String(labels.get(id) || id);

  // ⚠️ THE WINDOW IS THE INTERSECTION, NOT THE SUBSTITUTED ONE. With no times
  // entered the pending side is all-day, so the intersection is the OTHER
  // room's real hours — the times somebody actually typed. Quoting the
  // substitution instead ("12:00 AM to 11:59 PM") would put hours on screen
  // that exist nowhere in the data.
  const window =
    Number.isFinite(fromMin) && Number.isFinite(toMin) && toMin > fromMin
      ? `${to12h(fromMin)} to ${to12h(toMin)}`
      : "";

  // Reads like the drag's warning (timelineCommit.js:222) on purpose: the same
  // event, told the same way, whichever surface caused it.
  const sentence = `${who} is now in ${labelRooms(Array.from(roomIds), labels)} at the same time`;
  const message = win.untimed
    ? `${sentence}${window ? `, ${window}` : ""}. This assignment has no start or end time, so it covers the whole day.`
    : `${sentence}${window ? `, ${window}` : ""}.`;

  // Picker suffix: short enough to sit inside a native <option> without pushing
  // the person's own name off the visible width.
  const note = `already in ${labelRooms(otherIds, labels)}${window ? ` · ${window}` : ""}`;

  return {
    key,
    who,
    roomIds: Array.from(roomIds),
    otherRoomIds: otherIds,
    roomLabels: otherIds.map(nameOf),
    fromMin: Number.isFinite(fromMin) ? fromMin : null,
    toMin: Number.isFinite(toMin) ? toMin : null,
    untimed: win.untimed,
    message,
    note,
  };
}

/**
 * The same answer for every CRNA in the picker, so a conflict is VISIBLE BEFORE
 * the operator selects the person rather than only after they press Save.
 *
 * ⚠️ MARKED, NEVER HIDDEN — and that is a clinical decision, not a style one.
 * The grid's dropdown removes an already-booked CRNA (assignmentRow.cells.js:48)
 * because it is filling a blank schedule. This modal is used mid-day to MOVE
 * somebody, and a legitimate move into a second room is exactly why the timeline
 * warns instead of blocking (timelineCommit.js:206). A hidden option makes a
 * real action impossible with no explanation on screen — the operator concludes
 * the app is broken and works around it somewhere the board cannot see.
 *
 * @returns Map(provider.id → conflict object) — only for providers that clash.
 */
export function pickerConflicts({ board, providers = [], ...ctx } = {}) {
  const out = new Map();
  for (const p of arr(providers)) {
    if (!p || !p.id) continue;
    const c = seatConflict({ ...ctx, board, ahpId: p.id, ahpName: p.name || "" });
    if (c) out.set(p.id, c);
  }
  return out;
}
